"use client";

import * as React from "react";

import { useWidgetMode } from "@/components/ui/widget/widget-mode-context";

declare global {
  interface Window {
    /**
     * Framework-agnostic entry point for external code (Vue, vanilla JS,
     * Angular, etc.) to open the Fiinder widget in chat mode and auto-send an
     * initial user message.
     */
    spark_fiinder_conversation?: (initialMessage: string) => void;
  }
}

export type GlobalApiBridgeProps = {
  /** Imperatively shows or hides the widget. */
  toggleVisibility: (next: boolean) => void;
};

/**
 * Installs `window.spark_fiinder_conversation` on mount and removes it on
 * unmount. The function opens the widget, switches it to chat mode, and
 * stores the message so the Chat component auto-submits it.
 */
export function GlobalApiBridge({ toggleVisibility }: GlobalApiBridgeProps) {
  const { openChatWithMessage } = useWidgetMode();

  const toggleVisibilityRef = React.useRef(toggleVisibility);
  const openChatWithMessageRef = React.useRef(openChatWithMessage);
  React.useEffect(() => {
    toggleVisibilityRef.current = toggleVisibility;
  }, [toggleVisibility]);
  React.useEffect(() => {
    openChatWithMessageRef.current = openChatWithMessage;
  }, [openChatWithMessage]);

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const handler = (initialMessage: string) => {
      toggleVisibilityRef.current(true);
      openChatWithMessageRef.current(initialMessage);
    };
    window.spark_fiinder_conversation = handler;

    return () => {
      if (window.spark_fiinder_conversation === handler) {
        delete window.spark_fiinder_conversation;
      }
    };
  }, []);

  return null;
}

GlobalApiBridge.displayName = "GlobalApiBridge";
